import React, { useState } from "react";
import DisplayMediaObject from "../MediaObjectSection/DisplayMediaObject";
import courtAppointmentStyle from "./courtAppointmentStyle";
import { dateFieldDefault, emailDefault, requiredFieldDefault } from "../../services/validationRules";
import _ from "lodash";
import FormElement, { FormElementPropsWithoutProduct } from "../Case/CaseForm/FormElement";
import SubmitButton from "../Button/SubmitButton";
import useForm from "../../hooks/useForm";
import { formValue } from "../../services/formServiceFunctions";
import { Grid, Box } from "@mui/material";
import ApiClient from "../../services/ApiClient";
import AdvoAssistData from "../AdvoAssist/AdvoAssistData";
import AdvoAssistActions from "../AdvoAssist/AdvoAssistActions";
import LegalbirdIoConfirm from "../Modal/LegalbirdIoConfirm";
import ContentBox from "../ContentBox/ContentBox";
import Button from "@mui/material/Button";
import { Link } from "react-router-dom";
import { getCaseLink } from "../../services/Product/ProductService/productService";
import { apiPost } from "../../services/Api/apiCall";
import { useMutation } from "@tanstack/react-query";
import { updateResource } from "../../services/ReactQuery/reactQueryService";
import { CourtAppointment as CourtAppointmentType } from "../../types/CourtAppointment";
import { UseForm } from "../../types/UseForm";
import { MediaObject } from "../../types/MediaObject";
import { SelectOption } from "../../types/SelectOption";
import * as stagesCase from "../../services/Stages/StagesCase";
import { ProductUnion } from "../../types/ProductUnion";
import dayjs from "dayjs";

type CourtAppointmentProps = {
  headline: string;
  product: ProductUnion;
  courtAppointment: CourtAppointmentType;
  refreshPage: Function;
};

export default function CourtAppointment({ headline, product, courtAppointment, refreshPage }: CourtAppointmentProps) {
  const [mediaObjectToPreview, setMediaObjectToPreview] = useState<MediaObject>();
  const [isOpen, setIsOpen] = useState("");
  const updateMutation = useMutation({
    mutationFn: updateResource
  });

  const getCourtDateTypeOptions = (product: ProductUnion): SelectOption[] => {
    switch (product.productCategory) {
      case "tenancyLaw":
      case "contractLaw":
      case "civilLaw":
      case "administrativeLaw":
        return [{ value: "hearing" }, { value: "hearingOfEvidence" }];
      case "employmentLaw":
        return [{ value: "settlement" }, { value: "trialEmploymentLaw" }];
      case "familyLaw":
        return [{ value: "divorce" }];
      case "trafficLaw":
        return [{ value: "trialTrafficLaw" }];
      default:
        return [];
    }
  };

  const initialValues = courtAppointment;
  const onSubmit = async ({ values }: UseForm["values"]) => {
    if (courtAppointment.courtDate?.id) {
      await updateMutation.mutateAsync({
        id: courtAppointment.courtDate.id,
        uri: "appointments",
        data: {
          appointmentDate: dayjs(formValue(values, "courtDate.appointmentDate")).format("YYYY-MM-DD"),
          appointmentTime: formValue(values, "courtDate.appointmentTime"),
        },
      });
    }

    //must happen before the put on court_appointments because a subscriber is listening that needs the person data
    if (courtAppointment.advoAssistIndependentLawyer && formValue(values, "attendeeType") === "individual") {
      await updateMutation.mutateAsync({
        id: courtAppointment.advoAssistIndependentLawyer.id,
        uri: "people",
        data: {
          gender: values.advoAssistIndependentLawyer.gender,
          givenName: values.advoAssistIndependentLawyer.givenName,
          familyName: values.advoAssistIndependentLawyer.familyName,
          email: values.advoAssistIndependentLawyer.email,
        },
      });
    }
    if (courtAppointment?.id) {
      await updateMutation.mutateAsync({
        id: courtAppointment.id,
        uri: "court_appointments",
        data: {
          courtDateType: values.courtDateType,
          attendeeType: values.attendeeType,
          customNotes: values.customNotes,
          personalAppearanceClient: values.personalAppearanceClient,
        },
      });
    }

    if (formValue(values, "attendeeType") === "advoAssist") {
      await apiPost("services/advo_assist/add_court_appointment/0", {
        courtAppointmentId: courtAppointment.id,
        appointmentType: values.courtDateType,
      });
    }

    refreshPage();
  };

  const useFormProps = useForm({
    initialValues,
    onSubmit,
    identifier: courtAppointment,
  });

  const formDefinition: FormElementPropsWithoutProduct[] = [
    {
      type: "ValidatorDateField",
      path: "courtDate.appointmentDate",
      translationPath: "courtAppointment.courtDate.appointmentDate",
      validators: [...dateFieldDefault, ...requiredFieldDefault],
      isDisabled: () => !!courtAppointment.advoAssist,
    },
    {
      type: "ValidatorTextField",
      path: "courtDate.appointmentTime",
      translationPath: "courtAppointment.courtDate.appointmentTime",
      validators: requiredFieldDefault,
      isDisabled: () => !!courtAppointment.advoAssist,
      additionalProps: {
        type: "time",
        InputLabelProps: {
          shrink: true,
        },
      },
    },
    {
      type: "ValidatorSelect",
      path: "courtDateType",
      translationPath: "courtAppointment.courtDateType",
      validators: requiredFieldDefault,
      options: ({ product }: { product: ProductUnion }) => getCourtDateTypeOptions(product),
      isDisabled: () => !!courtAppointment.advoAssist,
    },
    {
      type: "ValidatorSelect",
      path: "attendeeType",
      translationPath: "courtAppointment.attendeeType",
      validators: requiredFieldDefault,
      options: () => [{ value: "advoAssist" }, { value: "individual" }],
      isDisabled: () => !!courtAppointment.advoAssist,
    },
    {
      type: "ValidatorSelect",
      path: "advoAssistIndependentLawyer.gender",
      translationPath: "courtAppointment.advoAssistIndependentLawyer.gender",
      validators: requiredFieldDefault,
      options: () => [{ value: "male" }, { value: "female" }],
      isHidden: ({ values }: UseForm["values"]) => formValue(values, "attendeeType") !== "individual",
      isDisabled: () => !!courtAppointment.advoAssist,
    },
    {
      type: "ValidatorTextField",
      path: "advoAssistIndependentLawyer.givenName",
      translationPath: "courtAppointment.advoAssistIndependentLawyer.givenName",
      validators: requiredFieldDefault,
      isHidden: ({ values }: UseForm["values"]) => formValue(values, "attendeeType") !== "individual",
      isDisabled: () => !!courtAppointment.advoAssist,
    },
    {
      type: "ValidatorTextField",
      path: "advoAssistIndependentLawyer.familyName",
      translationPath: "courtAppointment.advoAssistIndependentLawyer.familyName",
      validators: requiredFieldDefault,
      isHidden: ({ values }: UseForm["values"]) => formValue(values, "attendeeType") !== "individual",
      isDisabled: () => !!courtAppointment.advoAssist,
    },
    {
      type: "ValidatorTextField",
      path: "advoAssistIndependentLawyer.email",
      translationPath: "courtAppointment.advoAssistIndependentLawyer.email",
      validators: [...requiredFieldDefault, ...emailDefault],
      isHidden: ({ values }: UseForm["values"]) => formValue(values, "attendeeType") !== "individual",
      isDisabled: () => !!courtAppointment.advoAssist,
    },
    {
      type: "ValidatorSelect",
      translationPath: "courtAppointment.personalAppearanceClient",
      path: `personalAppearanceClient`,
      isDisabled: ({ product }: { product: ProductUnion }) => (product.stage || 0) > stagesCase.STAGE_TRIAL,
      options: () => [{ value: "ordered" }, { value: "yes" }, { value: "no" }],
    },
    {
      type: "ValidatorTextField",
      path: "customNotes",
      translationPath: "courtAppointment.customNotes",
      helperText:
        "Bei AdvoAssist wird der Inhalt ins Feld “Beschreibung” geschrieben. Bei “Individueller Anwalt” wird der Inhalt ans Ende des TV-Briefings eingefügt.",
      isDisabled: () => !!courtAppointment.advoAssist,
      additionalProps: {
        multiline: true,
        fullWidth: true,
      },
    },
  ];

  const deleteCourtAppointment = async () => {
    if (!!courtAppointment.advoAssist) {
      return;
    }
    await ApiClient.delete(courtAppointment["@id"]);
    refreshPage();
  };

  const confirmationDateTimeString = `${dayjs(formValue(useFormProps.values, "courtDate.appointmentDate")).format(
    "DD.MM.YYYY"
  )} um ${formValue(useFormProps.values, "courtDate.appointmentTime")} Uhr`;

  return (
    <>
      <ContentBox headline={headline}>
        {!!courtAppointment.invitationCourtDocument && (
          <Box
            sx={courtAppointmentStyle.linked}
            onClick={() =>
              courtAppointment.invitationCourtDocument &&
              setMediaObjectToPreview(courtAppointment.invitationCourtDocument)
            }
          >
            {courtAppointment.invitationCourtDocument.description}
          </Box>
        )}
        <Grid container>
          {_.map(formDefinition, (formElementProps) => {
            const fullWidth = formElementProps.additionalProps && formElementProps.additionalProps.fullWidth;
            return (
              <Grid
                sx={courtAppointmentStyle.spacingRight}
                key={formElementProps.path}
                size={{
                  md: !fullWidth && 6,
                  xs: 12
                }}>
                <FormElement product={product} {...formElementProps} {...useFormProps} />
              </Grid>
            );
          })}
          {!courtAppointment.advoAssist && (
            <>
              <Grid sx={courtAppointmentStyle.spacingBottom} size={12} />
              <Grid
                sx={courtAppointmentStyle.spacingRight}
                size={{
                  md: 6,
                  xs: 12
                }}>
                <Button onClick={() => setIsOpen("delete")} fullWidth color="primary" variant="text">
                  Löschen
                </Button>
                <LegalbirdIoConfirm
                  handleClose={() => setIsOpen("")}
                  open={isOpen === "delete"}
                  handleConfirm={deleteCourtAppointment}
                  confirmText="Löschen"
                  headline="Terminelement löschen"
                  content='Wenn das Terminelement versehentlich erstellt wurde, kann es mit dem Klick auf "Löschen" gelöscht werden.'
                />
              </Grid>
              <Grid
                sx={courtAppointmentStyle.spacingRight}
                size={{
                  md: 6,
                  xs: 12
                }}>
                <SubmitButton
                  onClick={() => setIsOpen("submit")}
                  error={_.keys(useFormProps.errors).length > 0}
                  isLoading={useFormProps.isLoading}
                  disabled={_.keys(useFormProps.touchedValues).length === 0}
                  variant={"contained"}
                  type={"submit"}
                >
                  Speichern
                </SubmitButton>
                <LegalbirdIoConfirm
                  handleClose={() => setIsOpen("")}
                  open={isOpen === "submit"}
                  handleConfirm={useFormProps.handleSubmit}
                  confirmText={
                    formValue(useFormProps.values, "attendeeType") === "individual"
                      ? "Speichern"
                      : "Speichern und ausschreiben"
                  }
                  headline={
                    formValue(useFormProps.values, "attendeeType") === "individual"
                      ? "Gerichtstermin anlegen"
                      : "Gerichtstermin ausschreiben"
                  }
                  content={
                    formValue(useFormProps.values, "attendeeType") === "individual"
                      ? `Nach dem Klick auf “Speichern” wird zwar ein Gerichtstermin erstellt, dieser aber
                    NICHT bei AdvoAssist ausgeschrieben. WICHTIG: Diese Option ist nur für die Wahrnehmung von Gerichtsterminen
                    durch unsere internen Anwälte oder Kooperationsanwälten gedacht. Möchten Sie einen Gerichtstermin für
                    den ${confirmationDateTimeString} speichern?`
                      : `Nach dem Klick auf “Speichern und Ausschreiben” wird automatisch ein Gerichtstermin bei AdvoAssist
                   ausgeschrieben. Möchten Sie einen Gerichtstermin für den ${confirmationDateTimeString} ausschreiben?`
                  }
                />
              </Grid>
            </>
          )}

          {!!courtAppointment.advoAssist && !!courtAppointment.courtDate && (
            <>
              <AdvoAssistData
                advoAssist={courtAppointment.advoAssist}
                legalbirdAppointment={courtAppointment.courtDate}
              />
              <Grid sx={courtAppointmentStyle.spacingBottom} size={12} />
              <AdvoAssistActions
                advoAssist={courtAppointment.advoAssist}
                legalbirdAppointment={courtAppointment.courtDate}
                product={product}
                refreshPage={refreshPage}
              />
            </>
          )}
          <Grid
            sx={{ padding: "0.5rem" }}
            size={{
              md: 6,
              xs: 12
            }}>
            <Button
              sx={{ marginTop: 1 }}
              variant={"contained"}
              component={Link}
              to={getCaseLink(product) + "/handakte/" + courtAppointment.id}
              disabled={
                !!courtAppointment.courtDate?.appointmentDateTime
                  ? dayjs(courtAppointment.courtDate.appointmentDateTime).add(1, "day") < dayjs()
                  : false
              }
            >
              Handakte bearbeiten
            </Button>
          </Grid>
        </Grid>
      </ContentBox>
      <DisplayMediaObject mediaObject={mediaObjectToPreview} closeDialog={() => setMediaObjectToPreview(undefined)} />
    </>
  );
}
